/**
 * DV-TEMP-002: Heartbeats Demo Component
 * Live heartbeat status and QC summary with stage selection and URL persistence
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Database, RefreshCw, Copy, Check } from 'lucide-react';
import { ExecutionSelector } from './ExecutionSelector';
import { StageSelector } from './StageSelector';
import { HeartbeatsPanel } from './HeartbeatsPanel';
import { QCSummaryCard } from './QCSummaryCard';
import { TestBlock } from './TestBlock';
import { useHeartbeats } from '@/hooks/useHeartbeats';
import { useQcSummary } from '@/hooks/useQcSummary';
import { useTelemetry } from './useTelemetry';
import { useStatusReport } from './useStatusReport';
import { PermissionNotice, NotFoundNotice, ServerErrorNotice } from './ErrorNotices';
import { getDemoError, logDemoError } from '@/lib/demo-error-handler';
import type { LogEntry } from './LogsPanel';
import { Stage, type StageName } from '@/lib/shared-types';
import { cn } from '@/utils/cn';

export interface HeartbeatsDemoProps {
  executionId?: string;
  onExecutionChange?: (executionId: string) => void;
  onLog?: (entry: LogEntry) => void;
  className?: string;
  'data-testid'?: string;
}

// Default execution ID for demo (valid UUID format required by backend)
// Updated to use execution ID that has events in the database (5cad6696-21a0-42f6-91d3-53f382399294 has 15 events)
const DEFAULT_EXECUTION_ID = '5cad6696-21a0-42f6-91d3-53f382399294';

// Staleness thresholds (in milliseconds) - exported for consistency across components
export const HEARTBEAT_STALE_THRESHOLD = 30 * 1000; // 30 seconds - visual staleness indicators
export const HEARTBEAT_WARNING_THRESHOLD = 60 * 1000; // 1 minute - user-facing warnings
// Note: Different thresholds allow for progressive degradation:
// 30s = show stale icon, 60s = show warning message

// Parse stage from URL with schema validation
const parseStageFromUrl = (searchParams: URLSearchParams): StageName => {
  const stage = searchParams.get('stage');
  // Validate against schema-defined stages, default to 'script'
  return Stage.options.includes(stage as StageName) ? (stage as StageName) : 'script';
};

export function HeartbeatsDemo({
  executionId = DEFAULT_EXECUTION_ID,
  onExecutionChange,
  onLog,
  className,
  'data-testid': testId = 'heartbeats-demo'
}: HeartbeatsDemoProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse initial stage from URL
  const [selectedStage, setSelectedStage] = useState<StageName>(() => {
    return parseStageFromUrl(searchParams);
  });

  // DV-TEMP-005: Live mode state
  const [isLiveMode, setIsLiveMode] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // URL debouncing for rapid stage changes
  const urlUpdateTimeoutRef = useRef<NodeJS.Timeout>();

  // Log collection for status reports
  const recentLogsRef = useRef<LogEntry[]>([]);
  const recentApiCallsRef = useRef<LogEntry[]>([]);

  // Enhanced logging function that captures logs for status reports
  const enhancedOnLog = useCallback((entry: LogEntry) => {
    // Call original onLog if provided
    if (onLog) {
      onLog(entry);
    }

    // Collect logs for status reports (keep last 50)
    if (entry.meta?.source === 'heartbeats' || entry.text.toLowerCase().includes('heartbeat') || entry.text.toLowerCase().includes('qc')) {
      recentLogsRef.current.push(entry);
      if (recentLogsRef.current.length > 50) {
        recentLogsRef.current = recentLogsRef.current.slice(-50);
      }
    }

    // Collect API calls separately (keep last 20)
    if (entry.meta?.method && entry.meta?.path) {
      recentApiCallsRef.current.push(entry);
      if (recentApiCallsRef.current.length > 20) {
        recentApiCallsRef.current = recentApiCallsRef.current.slice(-20);
      }
    }
  }, [onLog]);

  // Initialize telemetry and status report
  const { mark, time } = useTelemetry({
    onLog: enhancedOnLog
  });

  const { buildHeartbeatsReport } = useStatusReport();

  // DV-TEMP-005: Dynamic polling intervals with jitter
  const heartbeatsInterval = useMemo(() => {
    const baseInterval = isLiveMode ? 2000 : 5000; // 2s Live, 5s Standard
    const jitter = Math.random() * 500 + 100; // 100-600ms jitter
    return baseInterval + jitter;
  }, [isLiveMode]);

  const qcInterval = useMemo(() => {
    const baseInterval = isLiveMode ? 10000 : 30000; // 10s Live, 30s Standard
    const jitter = Math.random() * 1000 + 200; // 200-1200ms jitter
    return baseInterval + jitter;
  }, [isLiveMode]);

  // Fetch data with dynamic intervals
  const {
    data: heartbeatsData,
    isLoading: heartbeatsLoading,
    error: heartbeatsErrorData,
    refetch: refetchHeartbeats
  } = useHeartbeats(executionId, {
    refetchInterval: heartbeatsInterval
  });

  const {
    data: qcSummary,
    isLoading: qcLoading,
    error: qcErrorData,
    refetch: refetchQc
  } = useQcSummary(executionId, selectedStage, {
    refetchInterval: qcInterval
  });

  // Handle stage selection with URL persistence and debouncing
  const handleStageChange = useCallback((stage: StageName) => {
    // Equality guard - prevent redundant updates
    if (stage === selectedStage) {
      return;
    }

    setSelectedStage(stage);

    // Debounce URL updates to prevent rapid changes
    if (urlUpdateTimeoutRef.current) {
      clearTimeout(urlUpdateTimeoutRef.current);
    }

    urlUpdateTimeoutRef.current = setTimeout(() => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('stage', stage);
      setSearchParams(newParams, { replace: true });
    }, 100); // 100ms debounce

    // Log stage change with correlation ID
    enhancedOnLog({
      id: `stage-change-${Date.now()}`,
      ts: new Date().toISOString(),
      level: 'info',
      text: 'Heartbeats stage changed',
      meta: {
        source: 'heartbeats',
        stage,
        previousStage: selectedStage,
        executionId,
        correlationId: `hb-stage-${Date.now()}`
      }
    });
  }, [selectedStage, searchParams, setSearchParams, enhancedOnLog, executionId]);

  // Sync URL changes back to state
  useEffect(() => {
    const urlStage = parseStageFromUrl(searchParams);
    if (urlStage !== selectedStage) {
      setSelectedStage(urlStage);
    }
  }, [searchParams, selectedStage]);

  // DV-TEMP-005: Handle Live mode toggle
  const handleLiveModeToggle = useCallback(() => {
    const newLiveMode = !isLiveMode;
    setIsLiveMode(newLiveMode);

    // Log Live mode toggle
    enhancedOnLog({
      id: `live-mode-toggle-${Date.now()}`,
      ts: new Date().toISOString(),
      level: 'info',
      text: `Heartbeats Live mode ${newLiveMode ? 'enabled' : 'disabled'}`,
      meta: {
        source: 'heartbeats',
        liveMode: newLiveMode,
        heartbeatsInterval: newLiveMode ? 2000 : 5000,
        qcInterval: newLiveMode ? 10000 : 30000,
        executionId,
        correlationId: `hb-live-${Date.now()}`
      }
    });
  }, [isLiveMode, enhancedOnLog, executionId]);

  // Log polling interval changes with effective intervals
  useEffect(() => {
    const baseHeartbeats = isLiveMode ? 2000 : 5000;
    const baseQc = isLiveMode ? 10000 : 30000;
    const heartbeatsJitter = Math.round(heartbeatsInterval - baseHeartbeats);
    const qcJitter = Math.round(qcInterval - baseQc);

    enhancedOnLog({
      id: `polling-interval-${Date.now()}`,
      ts: new Date().toISOString(),
      level: 'info',
      text: `Heartbeats polling updated: ${Math.round(heartbeatsInterval)}ms heartbeats (${baseHeartbeats}ms + ${heartbeatsJitter}ms jitter), ${Math.round(qcInterval)}ms QC (${baseQc}ms + ${qcJitter}ms jitter) - Live: ${isLiveMode}`,
      meta: {
        source: 'heartbeats',
        liveMode: isLiveMode,
        heartbeats: {
          base: baseHeartbeats,
          jitter: heartbeatsJitter,
          effective: Math.round(heartbeatsInterval)
        },
        qc: {
          base: baseQc,
          jitter: qcJitter,
          effective: Math.round(qcInterval)
        },
        executionId,
        correlationId: `hb-polling-${Date.now()}`
      }
    });
  }, [heartbeatsInterval, qcInterval, isLiveMode, enhancedOnLog, executionId]);

  // Auto-log API errors when they occur and ensure they get into the status report
  useEffect(() => {
    if (heartbeatsErrorData) {
      const logEntry = {
        id: `api-error-heartbeats-${Date.now()}`,
        ts: new Date().toISOString(),
        level: 'error' as const,
        text: `Heartbeats API error: ${heartbeatsErrorData.status} - ${heartbeatsErrorData.message}`,
        meta: {
          source: 'heartbeats',
          method: 'GET',
          path: '/api/demo/temporal/heartbeats',
          status: heartbeatsErrorData.status || 500,
          error: heartbeatsErrorData.message,
          elapsed: 0,
          correlationId: `hb-auto-error-${Date.now()}`
        }
      };

      // Add to both logs and API calls collections
      enhancedOnLog(logEntry);
      recentApiCallsRef.current.push(logEntry);

      // Keep collection size manageable
      if (recentApiCallsRef.current.length > 20) {
        recentApiCallsRef.current = recentApiCallsRef.current.slice(-20);
      }
    }
  }, [heartbeatsErrorData, enhancedOnLog]);

  useEffect(() => {
    if (qcErrorData) {
      const logEntry = {
        id: `api-error-qc-${Date.now()}`,
        ts: new Date().toISOString(),
        level: 'error' as const,
        text: `QC Summary API error: ${qcErrorData.status} - ${qcErrorData.message}`,
        meta: {
          source: 'heartbeats',
          method: 'GET',
          path: '/api/demo/qc/summary',
          status: qcErrorData.status || 500,
          error: qcErrorData.message,
          elapsed: 0,
          correlationId: `qc-auto-error-${Date.now()}`
        }
      };

      // Add to both logs and API calls collections
      enhancedOnLog(logEntry);
      recentApiCallsRef.current.push(logEntry);

      // Keep collection size manageable
      if (recentApiCallsRef.current.length > 20) {
        recentApiCallsRef.current = recentApiCallsRef.current.slice(-20);
      }
    }
  }, [qcErrorData, enhancedOnLog]);

  // Wrapped refresh with timing and API call logging
  const handleRefresh = useCallback(async () => {
    const correlationId = `hb-refresh-${Date.now()}`;

    enhancedOnLog({
      id: `refresh-start-${Date.now()}`,
      ts: new Date().toISOString(),
      level: 'info',
      text: 'Manual refresh triggered for heartbeats and QC data',
      meta: {
        source: 'heartbeats',
        action: 'refresh',
        executionId,
        selectedStage,
        correlationId
      }
    });

    try {
      await time('refresh_heartbeats_data', async () => {
        const [heartbeatsResult, qcResult] = await Promise.allSettled([
          refetchHeartbeats(),
          refetchQc()
        ]);

        // Log API call results with request IDs
        if (heartbeatsResult.status === 'fulfilled') {
          const requestId = (heartbeatsResult.value.data as any)?._requestId;
          enhancedOnLog({
            id: `api-call-${Date.now()}`,
            ts: new Date().toISOString(),
            level: 'info',
            text: 'Heartbeats API call completed',
            meta: {
              source: 'heartbeats',
              method: 'GET',
              path: '/api/demo/temporal/heartbeats',
              status: 200,
              requestId,
              correlationId
            }
          });
        }

        if (qcResult.status === 'fulfilled') {
          const requestId = (qcResult.value.data as any)?._requestId;
          enhancedOnLog({
            id: `api-call-qc-${Date.now()}`,
            ts: new Date().toISOString(),
            level: 'info',
            text: 'QC Summary API call completed',
            meta: {
              source: 'heartbeats',
              method: 'GET',
              path: '/api/demo/qc/summary',
              status: 200,
              requestId,
              correlationId
            }
          });
        }
      }, {
        executionId,
        stage: selectedStage,
        correlationId
      });
    } catch (error) {
      enhancedOnLog({
        id: `refresh-error-${Date.now()}`,
        ts: new Date().toISOString(),
        level: 'error',
        text: `Refresh failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        meta: {
          source: 'heartbeats',
          action: 'refresh',
          error: error instanceof Error ? error.message : 'Unknown error',
          executionId,
          selectedStage,
          correlationId
        }
      });
    }
  }, [refetchHeartbeats, refetchQc, time, executionId, selectedStage, enhancedOnLog]);

  // DV-TEMP-005: Error handling
  const heartbeatsError = useMemo(() => {
    if (!heartbeatsErrorData) return null;
    const demoErr = getDemoError(heartbeatsErrorData);
    if (demoErr && onLog) {
      const logEntry = logDemoError(demoErr);
      onLog(logEntry);
    }
    return demoErr;
  }, [heartbeatsErrorData, onLog]);

  const qcError = useMemo(() => {
    if (!qcErrorData) return null;
    const demoErr = getDemoError(qcErrorData);
    if (demoErr && onLog) {
      const logEntry = logDemoError(demoErr);
      onLog(logEntry);
    }
    return demoErr;
  }, [qcErrorData, onLog]);

  // DV-TEMP-005: Status report copy handler with collected logs and API calls
  const handleCopyStatusReport = useCallback(async () => {
    const correlationId = `hb-copy-${Date.now()}`;

    try {
      // Build heartbeats-specific status report with collected data
      const report = buildHeartbeatsReport(
        executionId,
        selectedStage,
        heartbeatsData?.heartbeats || [],
        qcSummary,
        recentLogsRef.current || [], // Use collected logs
        recentApiCallsRef.current || [], // Use collected API calls
        isLiveMode,
        { heartbeats: Math.round(heartbeatsInterval), qc: Math.round(qcInterval) },
        heartbeatsErrorData as any, // Pass heartbeats error for debugging
        qcErrorData as any // Pass QC error for debugging
      );

      await navigator.clipboard.writeText(report);

      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);

      enhancedOnLog({
        id: `heartbeats-copy-${Date.now()}`,
        ts: new Date().toISOString(),
        level: 'info',
        text: 'Heartbeats status report copied to clipboard',
        meta: {
          source: 'heartbeats',
          action: 'copy_status',
          reportSize: report.length,
          logsIncluded: recentLogsRef.current.length,
          apiCallsIncluded: recentApiCallsRef.current.length,
          executionId,
          selectedStage,
          liveMode: isLiveMode,
          correlationId
        }
      });
    } catch (error) {
      enhancedOnLog({
        id: `heartbeats-copy-error-${Date.now()}`,
        ts: new Date().toISOString(),
        level: 'error',
        text: `Failed to copy heartbeats status report: ${error}`,
        meta: {
          source: 'heartbeats',
          action: 'copy_status',
          error: error instanceof Error ? error.message : 'Unknown error',
          executionId,
          correlationId
        }
      });
    }
  }, [buildHeartbeatsReport, executionId, selectedStage, heartbeatsData, qcSummary, isLiveMode, heartbeatsInterval, qcInterval, enhancedOnLog]);

  // Check for stage mismatches and warnings
  const stageWarnings = useCallback(() => {
    const warnings: string[] = [];

    // Check if execution is not running
    if (heartbeatsData && heartbeatsData.status !== 'running') {
      warnings.push(`Execution is ${heartbeatsData.status} - heartbeats may be stale`);
    }

    // Check if selected stage has different heartbeat stage
    if (heartbeatsData?.heartbeats) {
      const stageHeartbeat = heartbeatsData.heartbeats.find(hb => hb.stage === selectedStage);
      if (stageHeartbeat && !stageHeartbeat.isActive) {
        warnings.push(`Stage ${selectedStage} is not currently active`);
      }
    }

    // Check for stale heartbeats (older than warning threshold)
    if (heartbeatsData?.heartbeats) {
      const now = new Date();
      const staleStages = heartbeatsData.heartbeats.filter(hb => {
        const heartbeatTime = new Date(hb.lastHeartbeat);
        return now.getTime() - heartbeatTime.getTime() > HEARTBEAT_WARNING_THRESHOLD;
      });

      if (staleStages.length > 0) {
        warnings.push(`${staleStages.length} stage(s) have stale heartbeats (>1 min old)`);
      }
    }

    return warnings;
  }, [heartbeatsData, selectedStage]);

  // Component mount telemetry and cleanup
  useEffect(() => {
    mark('heartbeats_demo_mount', {
      executionId,
      selectedStage
    });

    return () => {
      // Cleanup URL update timeout
      if (urlUpdateTimeoutRef.current) {
        clearTimeout(urlUpdateTimeoutRef.current);
      }

      mark('heartbeats_demo_unmount', {
        executionId,
        selectedStage
      });
    };
  }, [mark, executionId, selectedStage]);

  const warnings = stageWarnings();

  return (
    <div className={cn("space-y-6", className)} data-testid={testId}>
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2
            className="text-xl font-light"
            style={{ color: 'var(--accent)' }}
          >
            Heartbeats & QC Summary
          </h2>
          <div className="flex items-center gap-3">
            {/* DV-TEMP-005: Live Mode Toggle */}
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={isLiveMode}
                onChange={handleLiveModeToggle}
                className="w-4 h-4 rounded border transition-colors focus:outline-none focus:ring-2"
                style={{
                  borderColor: 'var(--border-default)',
                  ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
                }}
                data-testid="heartbeats-live-toggle"
                role="switch"
                aria-checked={isLiveMode}
                aria-label="Toggle Live mode for faster polling"
              />
              <span
                className="text-sm font-medium"
                style={{ color: 'var(--fg-primary)' }}
              >
                Live
              </span>
            </label>

            {/* DV-TEMP-005: Copy Status Report Button */}
            <button
              onClick={handleCopyStatusReport}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs border transition-colors focus:outline-none focus:ring-2"
              style={{
                backgroundColor: 'var(--bg-surface)',
                borderColor: 'var(--border-default)',
                color: 'var(--fg-primary)',
                ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
                ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--bg-surface)';
              }}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = 'var(--focus-ring)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
              data-testid="dv-status-copy"
              aria-label="Copy status report to clipboard"
            >
              {copySuccess ? (
                <Check className="w-3 h-3" style={{ color: 'var(--success)' }} />
              ) : (
                <Copy className="w-3 h-3" />
              )}
              Copy Status Report
            </button>
          </div>
        </div>
        <p
          className="text-sm"
          style={{ color: 'var(--fg-muted)' }}
        >
          Live stage status and quality control results for pipeline execution.
        </p>
      </div>

      {/* Execution Selector */}
      <ExecutionSelector
        executionId={executionId}
        onExecutionChange={onExecutionChange || (() => {})}
        data-testid={`${testId}-execution-selector`}
      />

      {/* Stage Selector */}
      <StageSelector
        selectedStage={selectedStage}
        onStageChange={handleStageChange}
        data-testid={`${testId}-stage-selector`}
      />

      {/* Warnings */}
      {warnings.length > 0 && (
        <div
          className="p-3 rounded border flex items-start gap-2 bg-opacity-20"
          style={{
            backgroundColor: 'var(--warning)',
            borderColor: 'var(--warning)',
            opacity: 0.9
          }}
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--warning)' }} />
          <div className="flex-1">
            <div className="text-sm font-medium mb-1" style={{ color: 'var(--warning)' }}>
              Stage Status Warnings
            </div>
            <ul className="text-xs space-y-1" style={{ color: 'var(--fg-primary)' }}>
              {warnings.map((warning, index) => (
                <li key={index}>• {warning}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* DV-TEMP-005: Error Handling - Unified Error Notices */}
      {heartbeatsError && (
        <>
          {heartbeatsError.type === 'permission_denied' && (
            <PermissionNotice
              error={heartbeatsError}
              onRetry={refetchHeartbeats}
              data-testid="heartbeats-permission-error"
            />
          )}
          {heartbeatsError.type === 'not_found' && (
            <NotFoundNotice
              error={heartbeatsError}
              executionId={executionId}
              onRetry={refetchHeartbeats}
              data-testid="heartbeats-not-found-error"
            />
          )}
          {(heartbeatsError.type === 'server_error' || heartbeatsError.type === 'network_error') && (
            <ServerErrorNotice
              error={heartbeatsError}
              onRetry={refetchHeartbeats}
              data-testid="heartbeats-server-error"
            />
          )}
        </>
      )}

      {qcError && (
        <>
          {qcError.type === 'permission_denied' && (
            <PermissionNotice
              error={qcError}
              onRetry={refetchQc}
              data-testid="qc-permission-error"
            />
          )}
          {qcError.type === 'not_found' && (
            <NotFoundNotice
              error={qcError}
              executionId={executionId}
              onRetry={refetchQc}
              data-testid="qc-not-found-error"
            />
          )}
          {(qcError.type === 'server_error' || qcError.type === 'network_error') && (
            <ServerErrorNotice
              error={qcError}
              onRetry={refetchQc}
              data-testid="qc-server-error"
            />
          )}
        </>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Heartbeats Panel */}
        <HeartbeatsPanel
          executionId={executionId}
          selectedStage={selectedStage}
          onStageSelect={handleStageChange}
          onLog={onLog}
          data-testid={`${testId}-heartbeats`}
        />

        {/* QC Summary Card */}
        <QCSummaryCard
          executionId={executionId}
          stage={selectedStage}
          onLog={onLog}
          data-testid={`${testId}-qc-summary`}
        />
      </div>

      {/* Statistics & Controls */}
      <TestBlock
        title="Data Status & Controls"
        description="Current data status and refresh controls"
        status={heartbeatsError || qcError ? 'error' : heartbeatsLoading || qcLoading ? 'warning' : 'success'}
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={heartbeatsLoading || qcLoading}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs border transition-colors focus:outline-none focus:ring-2"
              style={{
                backgroundColor: 'var(--bg-surface)',
                borderColor: 'var(--border-default)',
                color: 'var(--fg-primary)',
                ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
                ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
              }}
              onMouseEnter={(e) => {
                if (!heartbeatsLoading && !qcLoading) {
                  e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--bg-surface)';
              }}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = 'var(--focus-ring)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
              data-testid={`${testId}-refresh`}
            >
              <RefreshCw className={cn("w-3 h-3", (heartbeatsLoading || qcLoading) && "animate-spin")} />
              Refresh
            </button>
          </div>
        }
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div
            className="text-center p-3 rounded border"
            style={{
              backgroundColor: 'var(--bg-elevated)',
              borderColor: 'var(--border-subtle)'
            }}
          >
            <div
              className="text-lg font-mono"
              style={{ color: 'var(--accent)' }}
            >
              {heartbeatsData?.heartbeats.length || 0}
            </div>
            <div
              className="text-xs"
              style={{ color: 'var(--fg-muted)' }}
            >
              Active Stages
            </div>
          </div>

          <div
            className="text-center p-3 rounded border"
            style={{
              backgroundColor: 'var(--bg-elevated)',
              borderColor: 'var(--border-subtle)'
            }}
          >
            <div
              className="text-lg font-mono"
              style={{ color: 'var(--accent)' }}
            >
              {selectedStage}
            </div>
            <div
              className="text-xs"
              style={{ color: 'var(--fg-muted)' }}
            >
              Selected Stage
            </div>
          </div>

          <div
            className="text-center p-3 rounded border"
            style={{
              backgroundColor: 'var(--bg-elevated)',
              borderColor: 'var(--border-subtle)'
            }}
          >
            <div
              className="text-lg font-mono"
              style={{ color: heartbeatsData?.status === 'running' ? 'var(--success)' : 'var(--warning)' }}
            >
              {heartbeatsData?.status.toUpperCase() || 'N/A'}
            </div>
            <div
              className="text-xs"
              style={{ color: 'var(--fg-muted)' }}
            >
              Execution Status
            </div>
          </div>

          <div
            className="text-center p-3 rounded border"
            style={{
              backgroundColor: 'var(--bg-elevated)',
              borderColor: 'var(--border-subtle)'
            }}
          >
            <div
              className="text-lg font-mono"
              style={{ color: qcSummary ? 'var(--accent)' : 'var(--fg-muted)' }}
            >
              {qcSummary ? 'YES' : 'NO'}
            </div>
            <div
              className="text-xs"
              style={{ color: 'var(--fg-muted)' }}
            >
              QC Available
            </div>
          </div>
        </div>
      </TestBlock>

      {/* Demo Info */}
      <TestBlock
        title="Demo Information"
        description="Technical implementation details"
        status="info"
        defaultOpen={false}
      >
        <div
          className="space-y-3 text-sm"
          style={{ color: 'var(--fg-primary)' }}
        >
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4" style={{ color: 'var(--accent)' }} />
            <span>Uses <code style={{ color: 'var(--accent)' }}>useHeartbeats</code> and <code style={{ color: 'var(--accent)' }}>useQcSummary</code> hooks</span>
          </div>
          <div>
            <strong>Heartbeats API:</strong> <code style={{ color: 'var(--accent)' }}>/api/demo/temporal/heartbeats</code>
          </div>
          <div>
            <strong>QC Summary API:</strong> <code style={{ color: 'var(--accent)' }}>/api/demo/qc/summary</code>
          </div>
          <div>
            <strong>Auto Refresh:</strong> {isLiveMode ? '2s heartbeats, 10s QC (Live mode)' : '5s heartbeats, 30s QC (Standard mode)'}
          </div>
          <div>
            <strong>Live Mode:</strong> {isLiveMode ? 'Enabled - faster polling with jitter' : 'Disabled - standard polling intervals'}
          </div>
          <div>
            <strong>URL Persistence:</strong> Selected stage persisted in <code style={{ color: 'var(--accent)' }}>?stage</code> parameter
          </div>
          <div>
            <strong>Stage Warnings:</strong> Automatic detection of non-running executions and stage mismatches
          </div>
        </div>
      </TestBlock>
    </div>
  );
}